import type { AgfTvHitlisteRow } from "./tobis-market";

/** Eine einzelne Sendung im Balken, fuer den Tooltip. */
export interface TvDayShow {
  titel: string;
  startzeit: string;
  mio: number;
}

/** Ein Tag als Balken: die Sender sind die Segmente. */
export interface TvDayBar {
  /** ISO-Datum, traegt die Sortierung. */
  date: string;
  /** Summe aller Sender dieses Tages, fuer die Beschriftung. */
  total: number;
  /**
   * Sendername -> die Sendungen, aus denen sein Segment besteht, absteigend
   * nach Sehbeteiligung. Traegt nur den Tooltip: der Balken zeigt, WIE VIEL
   * Publikum auf einen Sender entfiel, erst die Sendungen sagen, WOMIT.
   *
   * Bewusst hier und nicht als zweite Abfrage: die Zeilen liegen ohnehin
   * vollstaendig im Store, und ein Tooltip darf nicht nachladen.
   */
  shows: Record<string, TvDayShow[]>;
  /** Sendername -> Sehbeteiligung in Mio. Fehlt ein Sender an diesem Tag, fehlt der Schluessel. */
  [sender: string]: string | number | Record<string, TvDayShow[]>;
}

export interface TvDayStack {
  bars: TvDayBar[];
  /** Sender in Stapelreihenfolge: der reichweitenstaerkste zuerst (unten). */
  senders: string[];
}

/**
 * Faltet die Tages-Top-10 zu einem Balken je Tag, aufgeteilt nach Sender.
 *
 * Der Sinn: die Sendungen wechseln taeglich, die Sender bleiben dieselben - eine
 * Aufteilung nach Sender ist deshalb die einzige, die ueber Tage hinweg
 * vergleichbar ist. In den getrennten Tagestabellen faellt nicht auf, dass am
 * 14.08.2026 die ARD mit 19,18 Mio vor dem ZDF (13,80) lag, waehrend das ZDF an
 * allen anderen erfassten Tagen fuehrt.
 *
 * Summiert wird die Sehbeteiligung der Top-10-Platzierungen eines Senders, NICHT
 * seine Tagesreichweite: die Quelle liefert nur die Top 10 des Tages, ein Sender
 * ohne Platzierung fehlt darin vollstaendig. Der Balken zeigt also "wie viel
 * Publikum steckt in den zehn groessten Sendungen des Tages, und wie verteilt es
 * sich" - eine Aussage ueber die Spitze, nicht ueber den ganzen Tag.
 *
 * Die Stapelreihenfolge kommt aus der Gesamtreichweite ueber ALLE Tage, nicht
 * aus der des jeweiligen Tages: sonst wechselten die Segmente von Balken zu
 * Balken die Position und die Flaechen waeren nicht mehr vergleichbar.
 *
 * `senderLimit` fasst die schwachen Sender zu einer Restgruppe zusammen. Ohne
 * das traegt das Diagramm sieben Segmente, von denen vier zusammen unter fuenf
 * Prozent liegen (gemessen 2026-08-17: ZDF 71 Platzierungen, ARD 48, RTL 28,
 * Kabel Eins 6, ProSieben 3, SAT.1 2, VOX 2).
 */
export function tvDayStack(
  rows: readonly AgfTvHitlisteRow[],
  audience: string,
  senderLimit = 3,
  restLabel = "Andere",
): TvDayStack {
  const relevant = rows.filter((r) => r.audience === audience && r.sehbeteiligungMio != null);
  if (relevant.length === 0) return { bars: [], senders: [] };

  const gesamtJeSender = new Map<string, number>();
  for (const r of relevant) {
    gesamtJeSender.set(r.sender, (gesamtJeSender.get(r.sender) ?? 0) + (r.sehbeteiligungMio ?? 0));
  }
  const rangfolge = [...gesamtJeSender.entries()].sort((a, b) => b[1] - a[1]).map(([s]) => s);
  const gefuehrt = new Set(rangfolge.slice(0, senderLimit));
  const hatRest = rangfolge.length > senderLimit;

  const jeTag = new Map<string, Map<string, number>>();
  const showsJeTag = new Map<string, Map<string, TvDayShow[]>>();
  for (const r of relevant) {
    const tag = jeTag.get(r.date) ?? new Map<string, number>();
    const key = gefuehrt.has(r.sender) ? r.sender : restLabel;
    tag.set(key, (tag.get(key) ?? 0) + (r.sehbeteiligungMio ?? 0));
    jeTag.set(r.date, tag);

    const shows = showsJeTag.get(r.date) ?? new Map<string, TvDayShow[]>();
    const liste = shows.get(key) ?? [];
    liste.push({ titel: r.titel, startzeit: r.startzeit, mio: r.sehbeteiligungMio ?? 0 });
    shows.set(key, liste);
    showsJeTag.set(r.date, shows);
  }

  const bars = [...jeTag.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, sender]) => {
      const shows: Record<string, TvDayShow[]> = {};
      for (const [name, liste] of showsJeTag.get(date) ?? []) {
        // Absteigend, damit im Tooltip die staerkste Sendung oben steht - sie
        // erklaert den groessten Teil des Segments.
        shows[name] = [...liste].sort((a, b) => b.mio - a.mio);
      }
      const bar: TvDayBar = { date, total: 0, shows };
      let total = 0;
      for (const [name, wert] of sender) {
        // Auf zwei Stellen: die Quelle liefert drei Nachkommastellen je Sendung,
        // aufsummiert entstuenden sonst Werte wie 17.889999999999997.
        bar[name] = Math.round(wert * 100) / 100;
        total += wert;
      }
      bar.total = Math.round(total * 100) / 100;
      return bar;
    });

  return {
    bars,
    senders: [...rangfolge.slice(0, senderLimit), ...(hatRest ? [restLabel] : [])],
  };
}
